<script lang="ts">
    import Button from "@components/button/Button.svelte"
    import HelpIcon from "@components/help/HelpIcon.svelte"
    import DottedActionBox from "@components/material/DottedActionBox.svelte"
    import Icon from "@components/material/Icon.svelte"
    import type { TabClass } from "@store/pageTracker"
    import ReadingTabExpandedCard from "./ReadingTabExpandedCard.svelte"

    export let tabList: TabClass[] = []
    export let selectedTab: TabClass | null | undefined = null
    export let selectedTabID = "abandoned"
    export let createTab: () => void = () => {}
    export let browseWithoutTab: () => void = () => {}
    export let collapsePanel: () => void = () => {}
</script>

<section class="expanded-tab-home" aria-label="Reading tabs home">
    <button
        type="button"
        class="expanded-close"
        aria-label="Collapse reading tabs"
        on:click={collapsePanel}
    >
        <Icon icon="close" />
    </button>

    {#if tabList.length}
        <div class="expanded-content has-tabs">
            <div class="read-heading">
                <span class="read-label">Read</span>
                <h1>My Tabs <HelpIcon path="using-tabs" /></h1>
                <p>
                    {#if selectedTabID === "abandoned"}
                        Choose a tab to continue from where you left off, or open the table of
                        contents to browse without saving your place.
                    {:else}
                        Your current tab is
                        <strong class:untitled={!selectedTab?.nameWithoutEmoji}
                            >{selectedTab?.displayName}</strong
                        >. Select another tab below, or browse without saving your place.
                    {/if}
                </p>
                <div class="read-actions">
                    <Button
                        color="white"
                        icon="table-of-contents"
                        aria-label="Browse the table of contents without saving your place"
                        go={browseWithoutTab}
                    >
                        Browse
                    </Button>
                </div>
            </div>

            <div class="read-tabs-panel">
                <div class="tabs-grid" aria-label="Reading tabs">
                    {#each tabList as tab (tab.id)}
                        <ReadingTabExpandedCard {tab} on:select={collapsePanel} />
                    {/each}
                    <DottedActionBox
                        class="create-tab-tile"
                        ariaLabel="Create new tab"
                        on:click={createTab}
                    >
                        <span class="create-tab-content">
                            <span class="create-tab-icon" aria-hidden="true">
                                <Icon icon="plus" />
                            </span>
                            <span class="create-tab-title">Create new tab</span>
                            <span class="create-tab-description">
                                Add another moving bookmark for a different place in the Course.
                            </span>
                        </span>
                    </DottedActionBox>
                </div>
            </div>
        </div>
    {:else}
        <div class="expanded-content empty">
            <div class="read-empty">
                <span class="read-label">Read</span>
                <h1>Begin your journey</h1>
                <p>
                    This app uses flexible tabs to help you bookmark your place and save your
                    progress. Create a dedicated tab for your daily reading, or choose to browse
                    freely.
                </p>
                <Button color="purple" icon="plus" go={createTab}>Create my first tab</Button>
                <Button color="white" icon="table-of-contents" go={browseWithoutTab}>Browse</Button>
            </div>
        </div>
    {/if}
</section>

<style>
    strong.untitled {
        font-style: italic;
    }

    .expanded-tab-home {
        container-name: expanded-tabs;
        container-type: inline-size;
        display: grid;
        min-width: 0;
        min-height: 100%;
        color: var(--color-grey-900);
        position: relative;
    }

    .expanded-close {
        appearance: none;
        position: fixed;
        top: calc(var(--safe-area-inset-top) + 0.85rem);
        right: max(0.85rem, var(--safe-area-inset-right, 0rem));
        z-index: 2;
        display: grid;
        place-items: center;
        width: 2.65rem;
        height: 2.65rem;
        border: 1px solid var(--color-purple-100);
        border-radius: 0.65rem;
        background: var(--color-grey-0);
        box-shadow: none;
        color: var(--color-purple-700);
        cursor: pointer;
        transition:
            transform 0.16s ease,
            border-color 0.16s ease,
            background-color 0.16s ease,
            color 0.16s ease;
    }

    .expanded-close:hover {
        border-color: var(--color-purple-200);
        background: var(--color-purple-50);
        color: var(--color-purple-800);
    }

    .expanded-close:active {
        transform: translateY(1px);
    }

    .expanded-close:focus-visible {
        outline: 2px solid var(--color-gold-400);
        outline-offset: 2px;
    }

    .expanded-close :global(svg) {
        width: 1.1rem;
        height: 1.1rem;
    }

    .expanded-content {
        width: min(100%, 72rem);
        min-height: 100%;
        margin: 0 auto;
    }

    .expanded-content.has-tabs {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: var(--spacing-blockspace, 1.5rem);
    }

    .expanded-content.empty {
        display: grid;
        place-items: center;
        width: min(100%, 32rem);
    }

    .read-heading {
        display: grid;
        justify-items: center;
        gap: 0.75rem;
        width: min(100%, 38rem);
        margin: 0 auto 0.5rem;
        padding: 1.25rem 1rem 0.5rem;
        text-align: center;
    }

    .read-empty {
        display: grid;
        justify-items: center;
        gap: 1rem;
        width: 100%;
        color: var(--color-grey-900);
        padding: 1.25rem 1rem;
        text-align: center;
    }

    .read-label {
        color: var(--color-purple-700);
        font-size: 0.75rem;
        font-weight: 700;
        letter-spacing: 0.025em;
        line-height: 1;
        text-transform: uppercase;
    }

    h1,
    p {
        margin: 0;
    }

    h1 {
        color: var(--color-grey-900);
        font-family: Lora, ui-serif, Georgia, Cambria, "Times New Roman", Times, serif;
        font-size: 2rem;
        font-weight: 500;
        line-height: 1.2;
        letter-spacing: 0;
    }

    p {
        max-width: 58ch;
        color: var(--color-grey-600);
        font-size: 1rem;
        line-height: 1.75;
    }

    p strong {
        color: var(--color-purple-700);
        font-weight: 700;
    }

    .read-tabs-panel {
        container-name: read-tabs;
        container-type: inline-size;
        display: grid;
        justify-items: center;
        gap: 1rem;
        width: 100%;
    }

    .tabs-grid {
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        align-items: stretch;
        gap: 1rem;
        width: 100%;
    }

    .create-tab-content {
        display: grid;
        justify-items: center;
        gap: 0.55rem;
        max-width: 20rem;
    }

    .create-tab-icon {
        display: grid;
        place-items: center;
        width: 2.75rem;
        height: 2.75rem;
        border-radius: 999px;
        background: var(--color-purple-100);
        color: var(--color-purple-800);
    }

    .create-tab-icon :global(svg) {
        width: 1.15rem;
        height: 1.15rem;
    }

    .create-tab-title {
        color: var(--color-grey-900);
        font-family: Lora, ui-serif, Georgia, Cambria, "Times New Roman", Times, serif;
        font-size: 1.35rem;
        font-weight: 500;
        line-height: 1.25;
        letter-spacing: 0;
    }

    .create-tab-description {
        color: var(--color-grey-600);
        font-size: 0.95rem;
        line-height: 1.55;
    }

    .read-actions {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: 0.75rem;
    }

    :global(.darkmode) .expanded-tab-home,
    :global(.darkmode) .read-empty {
        color: var(--color-grey-50);
    }

    :global(.darkmode) .expanded-close {
        border-color: var(--color-grey-700);
        background: var(--color-grey-800);
        color: var(--color-purple-100);
    }

    :global(.darkmode) .expanded-close:hover {
        border-color: var(--color-grey-600);
        background: var(--color-grey-700);
        color: var(--color-grey-0);
    }

    :global(.darkmode) .read-label,
    :global(.darkmode) p strong {
        color: var(--color-purple-200);
    }

    :global(.darkmode) h1 {
        color: var(--color-grey-50);
    }

    :global(.darkmode) p {
        color: var(--color-grey-200);
    }

    :global(.darkmode) .create-tab-icon {
        background: var(--color-grey-700);
        color: var(--color-purple-100);
    }

    :global(.darkmode) .create-tab-title {
        color: var(--color-grey-50);
    }

    :global(.darkmode) .create-tab-description {
        color: var(--color-grey-200);
    }

    @container read-tabs (min-width: 42rem) {
        .tabs-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
        }
    }

    @container read-tabs (min-width: 64rem) {
        .tabs-grid {
            grid-template-columns: repeat(3, minmax(0, 1fr));
        }
    }

    @container expanded-tabs (max-width: 38rem) {
        .read-heading,
        .read-empty {
            padding-inline: 0.5rem;
        }

        .read-actions,
        .read-actions :global(.app-button),
        .read-empty :global(.app-button) {
            width: 100%;
        }
    }
</style>
